import {
  AccountTree as GraphIcon,
  AttachFile as DocumentsIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Map as MapIcon,
  Folder as PortfolioIcon,
  ContentPasteSearch as ResearchIcon,
  Search as SearchIcon,
  Hub as CapabilityMapIcon,
  TrackChanges as AccountIntelligenceIcon,
  Explore as ExploreIcon,
  Lan as ValueChainIcon,
  Insights as AnalyticsIcon,
} from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { rememberedScopeAddress } from "../Analytics/scopeMemory";
import { COMPARE_PATH } from "../Compare/compareState";

interface SidebarProps {
  hideMenuItems?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ hideMenuItems = false }) => {
  const location = useLocation();
  const { logout, hasFeature } = useAuth();

  const allMenuItems = [
    // { icon: <DashboardIcon />, path: '/', tooltip: 'Dashboard' },
    {
      icon: <ResearchIcon />,
      path: "/research",
      tooltip: "Research Workflow",
      feature: ["research", "research_viewer"],
    },
    {
      icon: <ExploreIcon />,
      path: "/explore",
      tooltip: "Explore",
      feature: "products",
    },
    {
      icon: <ValueChainIcon />,
      path: "/value-chain",
      tooltip: "Value chain",
      feature: "products",
    },
    {
      icon: <AnalyticsIcon />,
      path: "/analytics",
      // Reopens the last scope of the session, else every product
      to: rememberedScopeAddress("/analytics"),
      tooltip: "Analytics",
      feature: "products",
    },
    {
      icon: <SearchIcon />,
      path: "/search",
      tooltip: "Search",
      feature: "search",
    },
    { icon: <MapIcon />, path: "/map", tooltip: "Map", feature: "map" },
    { icon: <GraphIcon />, path: "/graph", tooltip: "Graph", feature: "graph" },
    {
      icon: <DocumentsIcon />,
      path: "/documents",
      tooltip: "Documents",
      feature: "research",
    },
    {
      icon: <PortfolioIcon />,
      path: "/portfolios",
      tooltip: "Portfolios",
      feature: "portfolios",
    },
    {
      icon: <CapabilityMapIcon />,
      path: "/capability-map",
      tooltip: "Capability Map",
      feature: "capability_map",
    },
    {
      icon: <AccountIntelligenceIcon />,
      path: "/account-intelligence",
      tooltip: "Account Intelligence",
      feature: "account_intelligence",
    },
  ];

  const menuItems = allMenuItems.filter(
    (item) => !item.feature || hasFeature(item.feature),
  );

  // Whether the page shown is the entry's own. The compare page has no
  // entry: it is reached from Explore, whose entry is the active one there.
  const isCurrent = (path: string): boolean =>
    location.pathname === path ||
    (path === "/explore" && location.pathname === COMPARE_PATH);

  return (
    <Box
      data-sidebar
      sx={{
        width: 60,
        height: "100%",
        backgroundColor: "background.paper",
        borderRight: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 2,
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src="/aa_triangle_white.png"
          alt="Alpha Affinity Logo"
          style={{
            width: 32,
            height: 32,
            objectFit: "contain",
          }}
        />
      </Box>

      {/* Main navigation items */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {!hideMenuItems &&
          menuItems.map((item) => (
            <Tooltip key={item.path} title={item.tooltip} placement="right">
              <IconButton
                component={Link}
                to={item.to ?? item.path}
                sx={{
                  mb: 1,
                  color: (
                    item.path === "/portfolios"
                      ? location.pathname.startsWith("/portfolios") ||
                        location.pathname.startsWith("/portfolio")
                      : isCurrent(item.path)
                  )
                    ? "primary.main"
                    : "text.secondary",
                  backgroundColor: (
                    item.path === "/portfolios"
                      ? location.pathname.startsWith("/portfolios") ||
                        location.pathname.startsWith("/portfolio")
                      : isCurrent(item.path)
                  )
                    ? "action.selected"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                {item.icon}
              </IconButton>
            </Tooltip>
          ))}
      </Box>

      <Tooltip title="Settings" placement="right">
        <IconButton
          component={Link}
          to="/settings"
          sx={{
            mb: 1,
            color:
              location.pathname === "/settings"
                ? "primary.main"
                : "text.secondary",
            backgroundColor:
              location.pathname === "/settings"
                ? "action.selected"
                : "transparent",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Logout" placement="right">
        <IconButton
          onClick={logout}
          sx={{
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "action.hover",
              color: "error.main",
            },
          }}
        >
          <LogoutIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default Sidebar;
